import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { defer, Subject } from 'rxjs';
import { distinctUntilChanged, finalize, takeUntil } from 'rxjs/operators';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { GetServiceordrerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/get-serviceordrer-request';
import { UpdateServiceaftalePartnerRequest } from 'src/app/api/models/OK/Egis/Service/Models/Request/update-serviceaftale-partner-request';
import { PartnerService, ServiceAftaleService, ServiceordreService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { EditPartnerDialogData } from '../models';
import { PartnerBaseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-base-dto';

@Component({
    selector: 'app-edit-partner',
    templateUrl: './edit-partner.component.html',
    styleUrls: ['./edit-partner.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditPartnerComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  allPartners: PartnerBaseDto[];
  activepartners: PartnerBaseDto[];
  serviceaftale: ServiceaftaleDto;
  unsubscribe$ = new Subject<void>();
  today: Date = new Date();
  hasUpcomingPartner: boolean = false;
  activeOrderCount: number = 0;
  upcomingPartner: string = '';
  partnerValidFrom: Date = new Date();

  get partnerNo() {
    return this.form.get('partnerNo');
  }

  get fastOrdreNote() {
    return this.form.get('fastOrdreNote');
  }

  get validFrom() {
    return this.form.get('validFrom');
  }

  constructor(
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private serviceaftaleService: ServiceAftaleService,
    private fb: UntypedFormBuilder,
    private dialog: MatDialogRef<EditPartnerComponent>,
    private serviceordreService: ServiceordreService,
    private partnerService: PartnerService,
    @Inject(MAT_DIALOG_DATA) public data: EditPartnerDialogData
  ) {}

  ngOnInit(): void {
    this.getPartnere$.subscribe(
      (result) => {
        this.allPartners = result;
        this.activepartners = this.allPartners.filter((x) => this.isPartnerActive(x, new Date()));
      },
      (error) => {
        this.errorHandler.HandleError(error);
      }
    );

    this.serviceaftale = this.data.serviceaftale;
    this.hasUpcomingPartner = this.data.upcomingChanges.hasPartnerChanges;
    this.upcomingPartner = this.data.upcomingChanges.upcomingPartner?.name;
    this.partnerValidFrom = new Date(this.data.upcomingChanges.partnerValidFrom);

    this.form = this.fb.group({
      partnerNo: [this.serviceaftale.partner.partnerNo, [Validators.required]],
      fastOrdreNote: new UntypedFormControl({ value: this.serviceaftale.fastOrdreNote, disabled: true }, [Validators.maxLength(500)]),
      validFrom: [new Date(), [Validators.required]],
    });

    this.getActiveServiceordrer$.subscribe((orders) => {
      this.activeOrderCount = orders.serviceordrer?.filter((x) => new Date(x.deadline) > new Date())?.length;
    });

    this.partnerNo.valueChanges.pipe(takeUntil(this.unsubscribe$), distinctUntilChanged()).subscribe((partnerNo) => {
      const partner = this.activepartners.find((x) => x.partnerNo == partnerNo);
      if (partner) {
        this.fastOrdreNote.enable();
      }
    });
  }

  private isPartnerActive(partner: PartnerBaseDto, date: Date): boolean {
    return (
      (partner.validTo == null || new Date(partner.validTo) > date) && (partner.validFrom == null || new Date(partner.validFrom) < date)
    );
  }

  getPartnere$ = defer(() => {
    this.loader.show();
    return this.partnerService
      .getAllPartnere({
        caller: EGIS_SERVICE_CALLER,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        })
      );
  });

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  getActiveServiceordrer$ = defer(() => {
    const request: GetServiceordrerRequest = {
      pageTop: 50,
      pageSkip: 0,
      serviceordreStateDtoIds: [1, 2, 3, 9, 10, 11, 12, 13],
      serviceaftaleNoList: [this.serviceaftale.serviceAftaleNo],
      orderDir: 'DESC',
      orderBy: 'Ajourtid',
      showParked: true,
    };

    return this.serviceordreService.getServiceordrer({ caller: EGIS_SERVICE_CALLER, body: request }).pipe(takeUntil(this.unsubscribe$));
  });

  save() {
    const request: UpdateServiceaftalePartnerRequest = {
      serviceaftaleId: this.serviceaftale.id,
      partnerNo: this.partnerNo.value,
      fastOrdreNote: this.fastOrdreNote.value,
      rowVersion: this.serviceaftale.rowVersion,
      validFrom: this.validFrom.value.toOkDateString(),
    };

    this.loader.show();

    this.serviceaftaleService
      .updateServiceaftalePartner({
        caller: EGIS_SERVICE_CALLER,
        body: request,
      })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        })
      )
      .subscribe(
        (serviceaftale) => {
          this.dialog.close(serviceaftale);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        }
      );
  }

  cancel() {
    this.dialog.close(null);
  }
}
